import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Binoculars, Sparkle, TrendUp, PaperPlaneTilt } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PLATFORMS } from "../lib/meta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Explainer, Jargon } from "../components/friendly";

export default function Research() {
  const { activeClientId, activeClient, refreshOrg } = useApp();
  const [brand, setBrand] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();

  const toComposer = (brief) => navigate("/composer", { state: { brief } });

  const load = useCallback(() => {
    if (!activeClientId) return;
    api.get(`/clients/${activeClientId}/brand`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
    api.get(`/ai/research?client_id=${activeClientId}`).then((r) => setJobs(r.data));
  }, [activeClientId]);
  useEffect(load, [load]);

  const analyze = async (competitor) => {
    setBusy(competitor);
    try {
      await api.post("/ai/competitor-research", { client_id: activeClientId, competitor });
      refreshOrg(); load();
      toast.success(`Research on ${competitor} ready (1 credit)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Research failed");
    } finally { setBusy(""); }
  };

  const refreshTrends = async () => {
    setBusy("trends");
    try {
      await api.post("/ai/trend-briefing", { client_id: activeClientId });
      refreshOrg(); load();
      toast.success("Trend radar refreshed (1 credit)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trend briefing failed");
    } finally { setBusy(""); }
  };

  const competitors = [...new Set([...(brand?.competitors || []), ...jobs.filter((j) => j.type === "competitor").map((j) => j.competitor)])];
  const competitorJobs = jobs.filter((j) => j.type === "competitor");
  const trendJob = jobs.find((j) => j.type === "trends");

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="research-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Competitor Intelligence</h1>
        <p className="text-sm text-slate-500 mt-1">AI research for <b>{activeClient?.name}</b>, grounded in their Brand Intelligence. <span className="text-amber-600 font-medium"><Jargon tip="The analysis is real AI — but the competitor data source is simulated for now. It goes live once SEMrush/Socialinsider is connected.">Mock-first</Jargon> — SEMrush/Socialinsider data plugs in when connected.</span></p>
      </div>
      <Explainer id="research">Keep an eye on competitors and trends. The co-pilot analyzes their positioning, spots content gaps, and hands you post ideas — one click sends any idea straight to the Composer.</Explainer>

      <div className="bg-white border border-slate-200/80 rounded-xl p-5 card-float">
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-medium mb-3">Competitors (from Brand Intelligence)</p>
        <div className="flex flex-wrap gap-2">
          {competitors.map((c) => (
            <Button key={c} data-testid={`analyze-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} variant="outline" size="sm"
              className="rounded-full gap-1.5" disabled={busy === c} onClick={() => analyze(c)}>
              <Binoculars size={13} /> {busy === c ? "Analyzing…" : `${c} (1 credit)`}
            </Button>
          ))}
          {!competitors.length && <p className="text-[13px] text-slate-400">Add competitors in Brand Intelligence, or analyze one below.</p>}
        </div>
        <div className="mt-4 flex gap-2">
          <Input data-testid="custom-competitor-input" placeholder="Analyze any competitor…" value={custom} onChange={(e) => setCustom(e.target.value)} className="max-w-xs" />
          <Button data-testid="custom-competitor-button" variant="outline" className="rounded-lg gap-1.5" disabled={!custom.trim() || busy === custom}
            onClick={() => { analyze(custom.trim()); setCustom(""); }}>
            <Sparkle size={13} weight="fill" className="text-[hsl(9,69%,62%)]" /> Analyze
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-5 card-float" data-testid="trend-radar">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendUp size={18} weight="duotone" className="text-[hsl(9,69%,62%)]" />
            <h2 className="font-display font-bold text-base">Trend radar</h2>
            {trendJob && <span className="text-[11px] text-slate-400">updated {new Date(trendJob.created_at).toLocaleDateString()}</span>}
          </div>
          <Button data-testid="refresh-trends-button" variant="outline" size="sm" className="rounded-lg gap-1.5" disabled={busy === "trends"} onClick={refreshTrends}>
            <Sparkle size={13} weight="fill" className="text-[hsl(9,69%,62%)]" /> {busy === "trends" ? "Scanning…" : trendJob ? "Refresh (1 credit)" : "Scan trends (1 credit)"}
          </Button>
        </div>
        {trendJob ? (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {(trendJob.result?.trends || []).map((t, i) => (
              <div key={i} data-testid={`trend-card-${i}`} className="border border-slate-100 bg-slate-50/60 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] font-bold">{t.title}</p>
                  <span className="flex gap-1 shrink-0">
                    {(t.platforms || []).map((p) => <span key={p} className="w-2 h-2 rounded-full mt-1.5" style={{ background: PLATFORMS[p]?.color || "#999" }} title={PLATFORMS[p]?.label} />)}
                  </span>
                </div>
                <p className="mt-1 text-[12px] text-slate-500 leading-relaxed">{t.why_now}</p>
                <p className="mt-2 text-[12px] text-slate-700 leading-relaxed">💡 {t.post_idea}</p>
                <Button data-testid={`trend-to-composer-${i}`} size="sm" variant="outline" className="mt-3 rounded-lg gap-1.5 h-7 text-[11px]"
                  onClick={() => toComposer(`Trend: ${t.title}. ${t.post_idea}`)}>
                  <PaperPlaneTilt size={11} /> Send to Composer
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-[13px] text-slate-400">Scan for niche-relevant content trends with on-brand execution ideas. <span className="text-amber-600 font-medium">Mock-first — live trend feeds plug in later.</span></p>
        )}
      </div>

      {competitorJobs.map((j) => (
        <div key={j.job_id} data-testid={`research-result-${j.job_id}`} className="bg-white border border-slate-200/80 rounded-xl p-6 card-float">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display font-bold text-xl tracking-tight">{j.competitor}</h2>
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">AI analysis · mock research mode</span>
          </div>
          <p className="mt-2 text-[13px] text-slate-600 italic">{j.result?.positioning}</p>
          <div className="mt-5 grid sm:grid-cols-3 gap-5">
            {[["Strengths", j.result?.strengths, "text-emerald-600"], ["Weaknesses", j.result?.weaknesses, "text-red-500"], ["Content gaps to own", j.result?.content_gaps, "text-[hsl(9,55%,48%)]"]].map(([label, items, cls]) => (
              <div key={label}>
                <p className={`text-[11px] uppercase tracking-[0.15em] font-bold ${cls}`}>{label}</p>
                <ul className="mt-2 space-y-1.5">
                  {(items || []).map((s, i) => <li key={i} className="text-[12px] text-slate-600 leading-relaxed flex gap-1.5"><span className="text-slate-300">•</span>{s}</li>)}
                </ul>
              </div>
            ))}
          </div>
          {j.result?.counter_moves?.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4 grid sm:grid-cols-2 gap-3">
              {j.result.counter_moves.map((m, i) => (
                <div key={i} className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                  <p className="text-[12px] font-bold">↳ {m.title}</p>
                  <p className="mt-1 text-[12px] text-slate-500 leading-relaxed">{m.detail}</p>
                  <Button data-testid={`counter-to-composer-${j.job_id}-${i}`} size="sm" variant="outline" className="mt-3 rounded-lg gap-1.5 h-7 text-[11px]"
                    onClick={() => toComposer(`Counter-move vs ${j.competitor} — ${m.title}: ${m.detail}`)}>
                    <PaperPlaneTilt size={11} /> Send to Composer
                  </Button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-[10px] text-slate-300">{new Date(j.created_at).toLocaleString()} · {j.source}</p>
        </div>
      ))}
    </div>
  );
}
